import { ReactElement } from "react";
import { BillingPeriodDto } from "../../../../data/clients/ExternalApiClient";
import { formatDateShort, formatInt, formatMoney } from "../shared/formatters";
import { StatusPill } from "../shared/StatusPill";

interface Props {
  periods: BillingPeriodDto[];
  loading?: boolean;
}

export function BillingPeriodsTable({
  periods,
  loading,
}: Props): ReactElement {
  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="px-5 py-3 border-b border-slate-200">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
          Faktureringsperioder
        </div>
      </div>
      {loading ? (
        <div className="px-5 py-10 text-center text-xs text-slate-400">
          Indlæser…
        </div>
      ) : periods.length === 0 ? (
        <div className="px-5 py-10 text-center text-xs text-slate-400">
          Ingen faktureringsperioder endnu.
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold border-b border-slate-100">
              <th className="text-left px-5 py-2">Periode</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Kald</th>
              <th className="text-right px-3 py-2">Inkluderet</th>
              <th className="text-right px-3 py-2">Overforbrug</th>
              <th className="text-right px-3 py-2">Grundgebyr</th>
              <th className="text-right px-3 py-2">Overforbrugspris</th>
              <th className="text-right px-5 py-2">I alt</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((p, i) => (
              <tr
                key={i}
                className={`border-b border-slate-100 last:border-0 ${p.isCurrent ? "bg-slate-50/50" : ""}`}
              >
                <td className="px-5 py-3">
                  <div className="text-xs font-medium text-slate-900">
                    {formatDateShort(p.periodStart)}
                    {p.periodEnd && ` → ${formatDateShort(p.periodEnd)}`}
                  </div>
                  {p.isCurrent && (
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">
                      I gang
                    </div>
                  )}
                </td>
                <td className="px-3 py-3">
                  <StatusPill
                    status={p.status}
                    tone={p.status === "Open" ? "warn" : "neutral"}
                  />
                </td>
                <td className="px-3 py-3 text-right font-mono text-xs text-slate-900 tabular-nums">
                  {formatInt(p.callsTotal)}
                </td>
                <td className="px-3 py-3 text-right font-mono text-xs text-slate-500 tabular-nums">
                  {formatInt(p.callsIncluded)}
                </td>
                <td className="px-3 py-3 text-right font-mono text-xs tabular-nums">
                  <span
                    className={
                      p.callsOverage > 0 ? "text-amber-700" : "text-slate-400"
                    }
                  >
                    {formatInt(p.callsOverage)}
                  </span>
                </td>
                <td className="px-3 py-3 text-right font-mono text-xs text-slate-700 tabular-nums">
                  {formatMoney(p.baseFee, p.currency)}
                </td>
                <td className="px-3 py-3 text-right font-mono text-xs text-slate-700 tabular-nums">
                  {formatMoney(p.overage, p.currency)}
                </td>
                <td className="px-5 py-3 text-right font-mono text-xs font-semibold text-slate-900 tabular-nums">
                  {formatMoney(p.total, p.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
